import { useCallback, useEffect, useState } from "react";
//old import { ArrowDown, ArrowUp, Archive, ListChecks, Loader2, Pencil, Plus, Send, Trash2, Users } from "lucide-react";
import { ArrowDown, ArrowUp, Archive, ExternalLink, ListChecks, Loader2, Pencil, Plus, Send, Trash2, Users } from "lucide-react";
import api from "../../api/api";
//old import { Alert, Field, OrgModal, Panel, Pill, apiError, formatDate, useOrgStyles } from "../../components/org/orgUi";
import { Alert, Field, OrgModal, Panel, Pill, Toggle, apiError, formatDate, useOrgStyles } from "../../components/org/orgUi";
import InlineTableSkeleton from "../../components/layout/InlineTableSkeleton";

/* Cohorts and the program's suggestion list (Alicia, Oct 2). A program repeats
   every term; a cohort is one run of it (Fall 2026). The program keeps a default
   list of suggestions; a cohort sends that list, or its own copy, to all of its
   students at once. Shown on a program's page; only org admins change things. */

const MAX_SUGGESTIONS = 20;
const MAX_RESOURCES = 20;

function dateRange(start, end) {
    if (start && end) return `${formatDate(start)} – ${formatDate(end)}`;
    if (start) return `From ${formatDate(start)}`;
    if (end) return `Until ${formatDate(end)}`;
    return "No dates";
}

function plural(n, word) {
    return `${n} ${word}${n === 1 ? "" : "s"}`;
}

const SENDING_OFF = "Sending isn't available yet: Pono Pilot still has to finish a database update.";

function PanelTitle({ icon: Icon, children }) {
    return <span className="inline-flex items-center gap-2"><Icon size={18} /> {children}</span>;
}

function SuggestionList({ items, empty }) {
    const s = useOrgStyles();
    if (!items.length) return <p className={`text-sm ${s.muted}`}>{empty}</p>;
    return (
        <ol className="space-y-2">
            {items.map((it, i) => (
                <li key={it.TemplateSuggestionID || i} className={`rounded-xl border p-3 ${s.subPanel}`}>
                    <div className={`text-sm font-semibold ${s.text}`}>{i + 1}. {it.Title}</div>
                    {it.Description && <p className={`mt-1 whitespace-pre-line text-sm ${s.body}`}>{it.Description}</p>}
                </li>
            ))}
        </ol>
    );
}

// Program resources (Evan, Oct 9): links students see in My Coaching → Programs.
// The program's list has its own panel on the program page (OrgPrograms.js); a
// cohort's own copy is edited in its window below. Both use ResourceList and ResourceEditor.
export function ResourceList({ items, empty }) {
    const s = useOrgStyles();
    if (!items.length) return <p className={`text-sm ${s.muted}`}>{empty}</p>;
    return (
        <ol className="space-y-2">
            {items.map((it, i) => (
                <li key={it.ProgramResourceID || i} className={`rounded-xl border p-3 ${s.subPanel}`}>
                    <div className="flex items-start gap-1.5 text-sm">
                        <span className={`font-semibold tabular-nums ${s.text}`}>{i + 1}.</span>
                        <a href={it.Url} target="_blank" rel="noopener noreferrer" className={`flex min-w-0 items-start gap-1 ${s.btnLink}`}>
                            <span className="min-w-0 break-words">{it.Title}</span>
                            <ExternalLink size={14} className="mt-0.5 shrink-0" />
                        </a>
                    </div>
                    {it.Description && <p className={`mt-1 whitespace-pre-line break-words text-sm ${s.body}`}>{it.Description}</p>}
                </li>
            ))}
        </ol>
    );
}

//old export default function ProgramCohorts({ orgId, programId, programName, isOrgAdmin }) {
export default function ProgramCohorts({ orgId, programId, programName, isOrgAdmin, isPlatformAdmin, cohortsHidden, onHiddenChange }) {
    const s = useOrgStyles();
    const [data, setData] = useState(null);
    const [error, setError] = useState("");
    const [editingList, setEditingList] = useState(false);
    const [cohortForm, setCohortForm] = useState(null); // null | {} (new) | cohort
    const [openCohortId, setOpenCohortId] = useState(null);
    const [savingHidden, setSavingHidden] = useState(false);
    const [notice, setNotice] = useState("");

    const load = useCallback(async () => {
        try {
            const res = await api.get(`/org/${orgId}/programs/${programId}/cohorts`);
            setData(res.data);
        } catch (err) {
            setError(apiError(err, "Couldn't load this program's cohorts."));
        }
    }, [orgId, programId]);

    useEffect(() => { load(); }, [load]);

    const saveProgramList = async (items) => {
        const res = await api.put(`/org/${orgId}/programs/${programId}/suggestions`, { suggestions: items });
        setData(res.data);
        setEditingList(false);
    };

    const archive = async (cohort) => {
        //old if (!window.confirm(`Archive "${cohort.CohortName}"? Its ${plural(cohort.StudentCount, "student")} go back to having no cohort. Suggestions already sent stay with them.`)) return;
        if (!window.confirm(`Archive "${cohort.CohortName}"? Its ${plural(cohort.StudentCount, "client")} go back to having no cohort. Suggestions already sent stay with them.`)) return;
        try {
            await api.delete(`/org/${orgId}/cohorts/${cohort.CohortID}`);
            await load();
        } catch (err) {
            setError(apiError(err));
        }
    };

    // Pono Pilot admins only: hide this program's cohorts from the organization (Alicia, Oct 9)
    const setHidden = async (hidden) => {
        //old if (hidden && !window.confirm(`Hide ${programName}'s cohorts from the organization? Its admins and coaches stop seeing this program's cohorts and suggestion list. Students still go into cohorts, and you run them here.`)) return;
        //old if (hidden && !window.confirm(`Hide ${programName}'s cohorts from the organization? Its admins and coaches stop seeing this program's cohorts but keep its suggestion list. Students still go into cohorts (those with none go into one now), and you run them here.`)) return;
        if (hidden && !window.confirm(`Hide ${programName}'s cohorts from the organization? Its admins and coaches stop seeing this program's cohorts but keep its suggestion list. Clients still go into cohorts (those with none go into one now), and you run them here.`)) return;
        setSavingHidden(true);
        setNotice("");
        try {
            //old await api.post(`/org/admin/orgs/${orgId}/programs/${programId}/hide-cohorts`, { hidden });
            //old await onHiddenChange();
            const res = await api.post(`/org/admin/orgs/${orgId}/programs/${programId}/hide-cohorts`, { hidden });
            // hiding puts students with no cohort in one, so the list changes too
            await Promise.all([onHiddenChange(), load()]);
            const placed = res.data?.placed || 0;
            //old if (placed) setNotice(`The ${plural(placed, "student")} with no cohort ${placed === 1 ? "is" : "are"} in a cohort now.`);
            if (placed) setNotice(`The ${plural(placed, "client")} with no cohort ${placed === 1 ? "is" : "are"} in a cohort now.`);
        } catch (err) {
            setError(apiError(err));
        } finally {
            setSavingHidden(false);
        }
    };

    if (!data) {
        return (
            <>
                <Panel title={<PanelTitle icon={ListChecks}>Suggestions for this program</PanelTitle>}>
                    <Alert>{error}</Alert>
                    {!error && <InlineTableSkeleton isDarkMode={s.dark} rows={2} cols={1} bordered={false} />}
                </Panel>
                {!error && (
                    <Panel title={<PanelTitle icon={Users}>Cohorts</PanelTitle>}>
                        <InlineTableSkeleton isDarkMode={s.dark} rows={2} cols={4} bordered={false} />
                    </Panel>
                )}
            </>
        );
    }

    const { cohorts, suggestions, unassignedCount, canSend } = data;
    return (
        <>
            <Panel
                title={<PanelTitle icon={ListChecks}>Suggestions for this program</PanelTitle>}
                actions={isOrgAdmin && (
                    <button type="button" className={s.btnSecondary} onClick={() => setEditingList(true)}>
                        {suggestions.length ? <><Pencil size={14} /> Edit list</> : <><Plus size={14} /> Add suggestions</>}
                    </button>
                )}
            >
                <p className={`mb-4 text-sm ${s.muted}`}>
                    {/* old The list each cohort sends its students. Students find them in My Coaching → Suggestions, marked as */}
                    {/* old from {programName} and their cohort, and accept or decline each one. */}
                    {/* old {" "}from {programName}{cohortsHidden ? "" : " and their cohort"}, and accept or decline each one. */}
                    {/* Alicia's wording (Oct 30), with client for student and the program's own name; only org admins fill the list, so coaches don't get its first sentence */}
                    {isOrgAdmin && "Provide a default list of specialized actions to each cohort. "}
                    Clients find these suggestions in My Coaching → Suggestions, Program Name: {programName}.
                </p>
                <SuggestionList items={suggestions}
                                //old empty="No suggestions yet. Add what every student in this program should do, like updating their resume." />
                                // only org admins can add to it, so coaches just get the first sentence
                                //old empty={`No suggestions yet.${isOrgAdmin ? " Add what every student in this program should do, like updating their resume." : ""}`} />
                                // (Alicia's wording, Oct 30)
                                empty={`No suggestions yet.${isOrgAdmin ? " Add what every client in this program should do, like updating their resume, meeting with a career counselor, etc." : ""}`} />
            </Panel>

            <Panel
                //old title={<PanelTitle icon={Users}>Cohorts</PanelTitle>}
                title={<span className="inline-flex flex-wrap items-center gap-2">
                    <PanelTitle icon={Users}>Cohorts</PanelTitle>
                    {cohortsHidden && <Pill tone="warning">Hidden from the organization</Pill>}
                </span>}
                actions={isOrgAdmin && (
                    <button type="button" className={s.btnPrimary} onClick={() => setCohortForm({})}><Plus size={14} /> New cohort</button>
                )}
            >
                <Alert onClose={() => setError("")}>{error}</Alert>
                <Alert kind="success" onClose={() => setNotice("")}>{notice}</Alert>
                {isPlatformAdmin && (
                    <div className={`mb-4 rounded-lg border px-3 ${s.subPanel}`}>
                        <Toggle label="Hide cohorts from the organization"
                                //old description="Students still go into cohorts. The organization's admins and coaches don't see this program's cohorts or suggestion list; Pono Pilot runs them here."
                                //old description="Students still go into cohorts: with none running or coming up, one is made for the fiscal year. The organization's admins and coaches don't see this program's cohorts but keep its suggestion list; Pono Pilot runs the cohorts here."
                                description="Clients still go into cohorts: with none running or coming up, one is made for the fiscal year. The organization's admins and coaches don't see this program's cohorts but keep its suggestion list; Pono Pilot runs the cohorts here."
                                checked={!!cohortsHidden} onChange={setHidden} disabled={savingHidden} />
                    </div>
                )}
                <p className={`mb-4 text-sm ${s.muted}`}>
                    {/* old A cohort is one run of this program, like Fall 2026. Students who join the program go into the cohort */}
                    {/* old running that day, or the next one to start. */}
                    {/* Alicia's wording (Oct 30), with client for student; the last sentence is what placement already does */}
                    {/* only org admins make cohorts and move clients between them, so coaches get just the last sentence (as with the suggestion list) */}
                    {isOrgAdmin && "You can group your clients into cohorts, to provide specific tasks and report on separately. If you want clients showing up on different reports, please put them in a different cohort. "}
                    Clients default to the current cohort, or the next one scheduled to start.
                    {cohortsHidden && " With none, one is made for the fiscal year (named like FY2027). Once this program's list has been sent, new cohorts are sent too."}
                </p>
                {isOrgAdmin && !canSend && (
                    <p className={`mb-4 rounded-lg border px-3 py-2 text-sm ${s.subPanel} ${s.body}`}>{SENDING_OFF}</p>
                )}
                {unassignedCount > 0 && cohorts.length > 0 && (
                    <p className={`mb-4 text-sm ${s.body}`}>
                        {/* old {plural(unassignedCount, "enrolled student")} {unassignedCount === 1 ? "isn't" : "aren't"} in a cohort yet.{isOrgAdmin ? " Open a cohort to add them." : ""} */}
                        {plural(unassignedCount, "enrolled client")} {unassignedCount === 1 ? "isn't" : "aren't"} in a cohort yet.{isOrgAdmin ? " Open a cohort to add them." : ""}
                    </p>
                )}
                {cohorts.length === 0 ? (
                    //old <p className={`text-sm ${s.muted}`}>No cohorts yet.{isOrgAdmin ? " Use New cohort to add this term's group." : ""}</p>
                    // Alicia's line (Oct 30); org admins still have New cohort at the top of the panel
                    <p className={`text-sm ${s.muted}`}>No cohorts yet.</p>
                ) : (
                    <div className="relative overflow-x-auto">
                        <table className="w-full min-w-[640px]">
                            <thead>
                                <tr>
                                    <th className={s.th}>Cohort</th>
                                    <th className={s.th}>Dates</th>
                                    {/* old <th className={s.th}>Students</th> */}
                                    <th className={s.th}>Clients</th>
                                    {/* old <th className={s.th}>Suggestions</th> */}
                                    <th className={s.th}>Suggestions & resources</th>
                                    <th className={s.th}><span className="sr-only">Actions</span></th>
                                </tr>
                            </thead>
                            <tbody>
                                {cohorts.map((c) => (
                                    <tr key={c.CohortID} className={`border-t ${s.row}`}>
                                        <td className={s.td}>
                                            <span className={`font-semibold ${s.text}`}>{c.CohortName}</span>
                                            {c.IsCurrent ? <span className="ml-2"><Pill tone="primary">Running</Pill></span> : null}
                                            {c.Ended ? <span className="ml-2"><Pill>Ended</Pill></span> : null}
                                        </td>
                                        <td className={`${s.td} whitespace-nowrap`}>{dateRange(c.StartDate, c.EndDate)}</td>
                                        <td className={`${s.td} tabular-nums`}>{c.StudentCount}</td>
                                        <td className={s.td}>
                                            <span className="inline-flex flex-wrap gap-1.5">
                                                {c.SentOn ? <Pill tone="success">Sent {formatDate(c.SentOn)}</Pill> : <Pill>Not sent</Pill>}
                                                {c.Customized ? <Pill tone="warning">Own list</Pill> : null}
                                                {c.ResourcesCustomized ? <Pill tone="warning">Own resources</Pill> : null}
                                            </span>
                                        </td>
                                        <td className={`${s.td} text-right`}>
                                            <span className="inline-flex flex-wrap justify-end gap-2">
                                                <button type="button" className={s.btnSm} onClick={() => setOpenCohortId(c.CohortID)}>Open</button>
                                                {isOrgAdmin && (
                                                    <>
                                                        <button type="button" className={s.btnSm} onClick={() => setCohortForm(c)} aria-label={`Edit ${c.CohortName}`} title="Edit"><Pencil size={12} /></button>
                                                        <button type="button" className={s.btnSm} onClick={() => archive(c)} aria-label={`Archive ${c.CohortName}`} title="Archive"><Archive size={12} /></button>
                                                    </>
                                                )}
                                            </span>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </Panel>

            {editingList && (
                <SuggestionEditor
                    title={`Suggestions for ${programName}`}
                    //old hint="Every cohort sends this list unless it has its own. Changing it doesn't change what students already have."
                    hint="Every cohort sends this list unless it has its own. Changing it doesn't change what clients already have."
                    initial={suggestions}
                    allowEmpty
                    onSave={saveProgramList}
                    onClose={() => setEditingList(false)}
                />
            )}
            {cohortForm && (
                <CohortForm orgId={orgId} programId={programId} cohort={cohortForm}
                            onClose={() => setCohortForm(null)}
                            onSaved={async () => { setCohortForm(null); await load(); }} />
            )}
            {openCohortId && (
                <CohortDetail
                    orgId={orgId}
                    cohortId={openCohortId}
                    programName={programName}
                    isOrgAdmin={isOrgAdmin}
                    unassignedCount={unassignedCount}
                    otherCohorts={cohorts.filter((c) => c.CohortID !== openCohortId)}
                    onChanged={load}
                    onClose={() => setOpenCohortId(null)}
                />
            )}
        </>
    );
}

// A program that hides its cohorts (Alicia, Oct 9): its organization keeps the program's suggestion
// list (Oct 2: the list is the program's, like its resources) and sends it to everyone in the program.
// The backend sends through each student's cohort; no cohort rows or counts are shown here.
export function ProgramSuggestions({ orgId, programId, programName, isOrgAdmin }) {
    const s = useOrgStyles();
    const [data, setData] = useState(null);
    const [error, setError] = useState("");
    const [notice, setNotice] = useState("");
    const [editing, setEditing] = useState(false);
    const [busy, setBusy] = useState(false);

    const load = useCallback(async () => {
        try {
            const res = await api.get(`/org/${orgId}/programs/${programId}/suggestions`);
            setData(res.data);
        } catch (err) {
            setError(apiError(err, "Couldn't load this program's suggestions."));
        }
    }, [orgId, programId]);

    useEffect(() => { load(); }, [load]);

    const save = async (items) => {
        const res = await api.put(`/org/${orgId}/programs/${programId}/suggestions`, { suggestions: items });
        setData(res.data);
        setEditing(false);
        //old setNotice("The list is saved. Use Send to give it to the program's students.");
        setNotice("The list is saved. Use Send to give it to the program's clients.");
    };

    const title = <PanelTitle icon={ListChecks}>Suggestions for this program</PanelTitle>;
    if (!data) {
        return (
            <Panel title={title}>
                <Alert>{error}</Alert>
                {!error && <InlineTableSkeleton isDarkMode={s.dark} rows={2} cols={1} bordered={false} />}
            </Panel>
        );
    }

    const { suggestions, canSend, students, someStudentsGetOtherList } = data;
    const send = async () => {
        // nobody in it yet: Send still marks the list sent, so the students who join get it (like a cohort's Send)
        const question = students
            //old ? `Send ${plural(suggestions.length, "suggestion")} to the ${plural(students, "student")} in ${programName}? Students only get the ones they don't have yet. Students who join later get them too.`
            ? `Send ${plural(suggestions.length, "suggestion")} to the ${plural(students, "client")} in ${programName}? Clients only get the ones they don't have yet. Clients who join later get them too.`
            //old : `Send ${programName}'s list now? Nobody is in it yet; students who join get it.`;
            : `Send ${programName}'s list now? Nobody is in it yet; clients who join get it.`;
        if (!window.confirm(question)) return;
        setBusy(true);
        setError("");
        setNotice("");
        try {
            const res = await api.post(`/org/${orgId}/programs/${programId}/suggestions/send`);
            setNotice(res.data.created ? `Sent ${plural(res.data.created, "suggestion")}.`
                : res.data.students ? "Everyone in this program already has these suggestions."
                    //old : "Marked as sent. Students who join get the list.");
                    : "Marked as sent. Clients who join get the list.");
            await load();
        } catch (err) {
            setError(apiError(err));
        } finally {
            setBusy(false);
        }
    };

    return (
        <>
            <Panel
                title={title}
                actions={isOrgAdmin && <>
                    <button type="button" className={s.btnSecondary} onClick={() => setEditing(true)} disabled={busy}>
                        {suggestions.length ? <><Pencil size={14} /> Edit list</> : <><Plus size={14} /> Add suggestions</>}
                    </button>
                    <button type="button" className={s.btnPrimary} onClick={send} disabled={busy || !canSend || !suggestions.length}>
                        {/* old {busy ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />} {students ? `Send to ${plural(students, "student")}` : "Send"} */}
                        {busy ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />} {students ? `Send to ${plural(students, "client")}` : "Send"}
                    </button>
                </>}
            >
                <Alert onClose={() => setError("")}>{error}</Alert>
                <Alert kind="success" onClose={() => setNotice("")}>{notice}</Alert>
                <p className={`mb-4 text-sm ${s.muted}`}>
                    {/* old The list this program sends its students. Students find them in My Coaching → Suggestions, marked as */}
                    The list this program sends its clients. Clients find them in My Coaching → Suggestions, marked as
                    from {programName}, and accept or decline each one.
                    {/* old {someStudentsGetOtherList && " Some students get a different list Pono Pilot set up for them."} */}
                    {someStudentsGetOtherList && " Some clients get a different list Pono Pilot set up for them."}
                </p>
                {isOrgAdmin && !canSend && (
                    <p className={`mb-4 rounded-lg border px-3 py-2 text-sm ${s.subPanel} ${s.body}`}>{SENDING_OFF}</p>
                )}
                <SuggestionList items={suggestions}
                                //old empty={`No suggestions yet.${isOrgAdmin ? " Add what every student in this program should do, like updating their resume." : ""}`} />
                                // (Alicia's wording, Oct 30, as on a program with cohorts)
                                empty={`No suggestions yet.${isOrgAdmin ? " Add what every client in this program should do, like updating their resume, meeting with a career counselor, etc." : ""}`} />
            </Panel>
            {editing && (
                <SuggestionEditor
                    title={`Suggestions for ${programName}`}
                    //old hint="Changing it doesn't change what students already have. Send gives students the items they don't have yet."
                    hint="Changing it doesn't change what clients already have. Send gives clients the items they don't have yet."
                    initial={suggestions}
                    allowEmpty
                    onSave={save}
                    onClose={() => setEditing(false)}
                />
            )}
        </>
    );
}

function CohortForm({ orgId, programId, cohort, onClose, onSaved }) {
    const s = useOrgStyles();
    const isNew = !cohort.CohortID;
    const [form, setForm] = useState({
        cohortName: cohort.CohortName || "",
        startDate: cohort.StartDate ? String(cohort.StartDate).slice(0, 10) : "",
        endDate: cohort.EndDate ? String(cohort.EndDate).slice(0, 10) : "",
    });
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState("");
    const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

    const save = async (e) => {
        e.preventDefault();
        setSaving(true);
        setError("");
        try {
            const body = { cohortName: form.cohortName, startDate: form.startDate || null, endDate: form.endDate || null };
            if (isNew) await api.post(`/org/${orgId}/programs/${programId}/cohorts`, body);
            else await api.put(`/org/${orgId}/cohorts/${cohort.CohortID}`, body);
            await onSaved();
        } catch (err) {
            setError(apiError(err));
            setSaving(false);
        }
    };

    return (
        <OrgModal isOpen onClose={onClose} title={isNew ? "New cohort" : `Edit ${cohort.CohortName}`}
                  footer={<>
                      <button type="button" className={s.btnSecondary} onClick={onClose}>Cancel</button>
                      <button type="submit" form="cohort-form" className={s.btnPrimary} disabled={saving}>
                          {saving && <Loader2 size={14} className="animate-spin" />} {isNew ? "Add cohort" : "Save"}
                      </button>
                  </>}>
            <Alert>{error}</Alert>
            <form id="cohort-form" onSubmit={save} className="space-y-4">
                <Field label="Name" htmlFor="cohort-name" hint="For example Fall 2026.">
                    <input id="cohort-name" className={s.input} value={form.cohortName} onChange={set("cohortName")} maxLength={200} required />
                </Field>
                <div className="grid gap-4 sm:grid-cols-2">
                    <Field label="Starts" htmlFor="cohort-start">
                        <input id="cohort-start" type="date" className={s.input} value={form.startDate} onChange={set("startDate")} />
                    </Field>
                    <Field label="Ends" htmlFor="cohort-end">
                        <input id="cohort-end" type="date" className={s.input} value={form.endDate} onChange={set("endDate")} />
                    </Field>
                </div>
                {/* old <p className={`text-xs ${s.muted}`}>Students who join after the end date don't get this cohort's suggestions.</p> */}
                <p className={`text-xs ${s.muted}`}>Clients who join after the end date don't get this cohort's suggestions.</p>
            </form>
        </OrgModal>
    );
}

function CohortDetail({ orgId, cohortId, programName, isOrgAdmin, unassignedCount, otherCohorts, onChanged, onClose }) {
    const s = useOrgStyles();
    const [data, setData] = useState(null);
    const [error, setError] = useState("");
    const [notice, setNotice] = useState("");
    const [busy, setBusy] = useState(false);
    const [editing, setEditing] = useState(false);
    const [editingResources, setEditingResources] = useState(false);

    const load = useCallback(async () => {
        try {
            const res = await api.get(`/org/${orgId}/cohorts/${cohortId}`);
            setData(res.data);
        } catch (err) {
            setError(apiError(err, "Couldn't load this cohort."));
        }
    }, [orgId, cohortId]);

    useEffect(() => { load(); }, [load]);

    // Runs a change, then shows the cohort (from the reply, or reloaded) and refreshes the cohort list.
    const act = async (fn, message) => {
        setBusy(true);
        setError("");
        setNotice("");
        try {
            const res = await fn();
            if (res?.data?.cohort) setData(res.data);
            else await load();
            if (message) setNotice(typeof message === "function" ? message(res?.data) : message);
            await onChanged();
        } catch (err) {
            setError(apiError(err));
        } finally {
            setBusy(false);
        }
    };

    if (!data) {
        return (
            <OrgModal isOpen onClose={onClose} title="Cohort" wide>
                <Alert>{error}</Alert>
                {!error && <InlineTableSkeleton isDarkMode={s.dark} rows={3} cols={2} bordered={false} />}
            </OrgModal>
        );
    }

    //old const { cohort, suggestions, customized, students, canSend } = data;
    const { cohort, suggestions, customized, students, canSend, resources = [], resourcesCustomized } = data;
    const name = `${programName} · ${cohort.CohortName}`;

    // The list editor replaces this window while it's open (two stacked windows would both close on Esc).
    if (editing) {
        const saveOwnList = async (items) => {
            const res = await api.put(`/org/${orgId}/cohorts/${cohortId}/suggestions`, { suggestions: items });
            setData(res.data);
            setEditing(false);
            setNotice("This cohort's list is saved.");
            await onChanged();
        };
        return (
            <SuggestionEditor
                title={`Suggestions for ${name}`}
                hint={customized
                    //old ? "This cohort's own list. Changing it doesn't change what students already have."
                    ? "This cohort's own list. Changing it doesn't change what clients already have."
                    : "Saving gives this cohort its own copy of the program's list. Other cohorts keep using the program's list."}
                initial={suggestions}
                onSave={saveOwnList}
                onClose={() => setEditing(false)}
            />
        );
    }
    // The resources editor replaces it the same way.
    if (editingResources) {
        const saveOwnResources = async (items) => {
            const res = await api.put(`/org/${orgId}/cohorts/${cohortId}/resources`, { resources: items });
            setData(res.data);
            setEditingResources(false);
            setNotice("This cohort's resources are saved.");
            await onChanged();
        };
        return (
            <ResourceEditor
                title={`Resources for ${name}`}
                hint={resourcesCustomized
                    ? "This cohort's own resources."
                    : "Saving gives this cohort its own copy of the program's resources. Other cohorts keep using the program's list."}
                initial={resources}
                onSave={saveOwnResources}
                onClose={() => setEditingResources(false)}
            />
        );
    }

    const send = () => {
        const until = cohort.EndDate ? ` until ${formatDate(cohort.EndDate)}` : "";
        const question = cohort.SentOn
            //old ? `Send ${cohort.CohortName}'s list again? Students only get the items they don't have yet.`
            ? `Send ${cohort.CohortName}'s list again? Clients only get the items they don't have yet.`
            : students.length
                //old ? `Send ${plural(suggestions.length, "suggestion")} to the ${plural(students.length, "student")} in ${cohort.CohortName}? `
                ? `Send ${plural(suggestions.length, "suggestion")} to the ${plural(students.length, "client")} in ${cohort.CohortName}? `
                  //old + `Students who join this cohort later${until} get them too.`
                  // nobody new gets an ended cohort's list (students moved in after the end don't get it)
                  //old + (cohort.Ended ? "" : `Students who join this cohort later${until} get them too.`)
                  + (cohort.Ended ? "" : `Clients who join this cohort later${until} get them too.`)
                //old : `Send ${cohort.CohortName}'s list now? Nobody is in it yet; students who join${until} get it.`;
                : cohort.Ended ? `Send ${cohort.CohortName}'s list now? Nobody is in it.`
                    //old : `Send ${cohort.CohortName}'s list now? Nobody is in it yet; students who join${until} get it.`;
                    : `Send ${cohort.CohortName}'s list now? Nobody is in it yet; clients who join${until} get it.`;
        if (!window.confirm(question)) return;
        act(() => api.post(`/org/${orgId}/cohorts/${cohortId}/send`),
            (r) => (r?.created ? `Sent ${plural(r.created, "suggestion")}.`
                : r?.students ? "Everyone in this cohort already has these suggestions."
                    //old : "Marked as sent. Students who join this cohort get the list."));
                    //old : `Marked as sent.${cohort.Ended ? "" : " Students who join this cohort get the list."}`));
                    : `Marked as sent.${cohort.Ended ? "" : " Clients who join this cohort get the list."}`));
    };
    const backToProgramList = () => {
        //old if (!window.confirm(`Use ${programName}'s list for ${cohort.CohortName} again? This cohort's own list goes away. Students keep what they already have.`)) return;
        if (!window.confirm(`Use ${programName}'s list for ${cohort.CohortName} again? This cohort's own list goes away. Clients keep what they already have.`)) return;
        act(() => api.delete(`/org/${orgId}/cohorts/${cohortId}/suggestions`), "This cohort uses the program's list again.");
    };
    const backToProgramResources = () => {
        if (!window.confirm(`Use ${programName}'s resources for ${cohort.CohortName} again? This cohort's own resources go away.`)) return;
        act(() => api.delete(`/org/${orgId}/cohorts/${cohortId}/resources`), "This cohort uses the program's resources again.");
    };
    const addUnassigned = () => act(() => api.post(`/org/${orgId}/cohorts/${cohortId}/students`, { unassigned: true }),
        //old (r) => `Added ${plural(r?.moved || 0, "student")}${r?.created ? ` and sent them ${plural(r.created, "suggestion")}` : ""}.`);
        (r) => `Added ${plural(r?.moved || 0, "client")}${r?.created ? ` and sent them ${plural(r.created, "suggestion")}` : ""}.`);
    const moveTo = (student, target) => {
        const to = otherCohorts.find((c) => String(c.CohortID) === String(target));
        if (!to) return;
        act(() => api.post(`/org/${orgId}/cohorts/${to.CohortID}/students`, { clientIds: [student.ClientID] }),
            (r) => `${student.FirstName} ${student.LastName} moved to ${to.CohortName}${r?.created ? ` and got ${plural(r.created, "suggestion")}` : ""}.`);
    };

    //old const sendLabel = cohort.SentOn ? "Send again" : students.length ? `Send to ${plural(students.length, "student")}` : "Send";
    const sendLabel = cohort.SentOn ? "Send again" : students.length ? `Send to ${plural(students.length, "client")}` : "Send";
    return (
        <OrgModal isOpen onClose={onClose} title={name} wide
                  footer={<>
                      <button type="button" className={s.btnSecondary} onClick={onClose}>Close</button>
                      {isOrgAdmin && (
                          <button type="button" className={s.btnPrimary} onClick={send}
                                  disabled={busy || !canSend || suggestions.length === 0}>
                              {busy ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />} {sendLabel}
                          </button>
                      )}
                  </>}>
            <div className="space-y-6">
                <div>
                    <Alert onClose={() => setError("")}>{error}</Alert>
                    <Alert kind="success" onClose={() => setNotice("")}>{notice}</Alert>
                    <p className={`text-sm ${s.muted}`}>
                        {dateRange(cohort.StartDate, cohort.EndDate)}
                        {cohort.SentOn
                            //old ? ` · Sent ${formatDate(cohort.SentOn)}. Students who join later get the list too.`
                            // only for the cohort new students go into (TakesNewStudents, worked out by the backend's placement)
                            //old ? ` · Sent ${formatDate(cohort.SentOn)}.${cohort.TakesNewStudents ? " Students who join later get the list too." : ""}`
                            ? ` · Sent ${formatDate(cohort.SentOn)}.${cohort.TakesNewStudents ? " Clients who join later get the list too." : ""}`
                            : " · Not sent yet."}
                    </p>
                    {isOrgAdmin && !canSend && (
                        <p className={`mt-3 rounded-lg border px-3 py-2 text-sm ${s.subPanel} ${s.body}`}>{SENDING_OFF}</p>
                    )}
                </div>

                <section>
                    <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
                        <h4 className={`flex flex-wrap items-center gap-2 text-sm font-bold ${s.text}`}>
                            What this cohort sends
                            <Pill tone={customized ? "warning" : "neutral"}>{customized ? "Its own list" : "The program's list"}</Pill>
                        </h4>
                        {isOrgAdmin && (
                            <span className="flex flex-wrap gap-2">
                                <button type="button" className={s.btnSm} onClick={() => setEditing(true)} disabled={busy}>
                                    <Pencil size={12} /> {customized ? "Edit this cohort's list" : "Change for this cohort"}
                                </button>
                                {customized && <button type="button" className={s.btnSm} onClick={backToProgramList} disabled={busy}>Use the program's list</button>}
                            </span>
                        )}
                    </div>
                    {/* old <SuggestionList items={suggestions} empty="Nothing to send yet. Add suggestions to the program's list first." /> */}
                    <SuggestionList items={suggestions} empty={`Nothing to send yet.${isOrgAdmin ? " Add suggestions to the program's list first." : ""}`} />
                </section>

                {/* Not sent: students' Programs page looks them up for their current cohort */}
                <section>
                    <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
                        <h4 className={`flex flex-wrap items-center gap-2 text-sm font-bold ${s.text}`}>
                            Resources this cohort sees
                            <Pill tone={resourcesCustomized ? "warning" : "neutral"}>{resourcesCustomized ? "Its own list" : "The program's list"}</Pill>
                        </h4>
                        {isOrgAdmin && (
                            <span className="flex flex-wrap gap-2">
                                <button type="button" className={s.btnSm} onClick={() => setEditingResources(true)} disabled={busy}>
                                    <Pencil size={12} /> {resourcesCustomized ? "Edit this cohort's resources" : "Change for this cohort"}
                                </button>
                                {resourcesCustomized && <button type="button" className={s.btnSm} onClick={backToProgramResources} disabled={busy}>Use the program's resources</button>}
                            </span>
                        )}
                    </div>
                    <ResourceList items={resources} empty="No resources yet. This cohort uses the program's resources, and the program has none." />
                </section>

                <section>
                    <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
                        {/* old <h4 className={`text-sm font-bold ${s.text}`}>Students ({students.length})</h4> */}
                        <h4 className={`text-sm font-bold ${s.text}`}>Clients ({students.length})</h4>
                        {isOrgAdmin && unassignedCount > 0 && (
                            <button type="button" className={s.btnSm} onClick={addUnassigned} disabled={busy}>
                                {/* old <Plus size={12} /> Add the {plural(unassignedCount, "student")} with no cohort */}
                                <Plus size={12} /> Add the {plural(unassignedCount, "client")} with no cohort
                            </button>
                        )}
                    </div>
                    {students.length === 0 ? (
                        //old <p className={`text-sm ${s.muted}`}>No students yet. Students who join the program while this cohort runs are added automatically.</p>
                        //old <p className={`text-sm ${s.muted}`}>No students yet.{cohort.TakesNewStudents ? " Students who join the program now are added automatically." : ""}</p>
                        <p className={`text-sm ${s.muted}`}>No clients yet.{cohort.TakesNewStudents ? " Clients who join the program now are added automatically." : ""}</p>
                    ) : (
                        <div className="relative overflow-x-auto">
                            <table className="w-full min-w-[480px]">
                                <thead>
                                    <tr>
                                        {/* old <th className={s.th}>Student</th> */}
                                        <th className={s.th}>Client</th>
                                        <th className={s.th}>Program suggestions they have</th>
                                        {isOrgAdmin && otherCohorts.length > 0 && <th className={s.th}>Move to</th>}
                                    </tr>
                                </thead>
                                <tbody>
                                    {students.map((st) => (
                                        <tr key={st.ClientID} className={`border-t ${s.row}`}>
                                            <td className={s.td}>{st.FirstName} {st.LastName}</td>
                                            <td className={`${s.td} tabular-nums`}>{st.Received}</td>
                                            {isOrgAdmin && otherCohorts.length > 0 && (
                                                <td className={s.td}>
                                                    <select className={`${s.input} max-w-[12rem] py-1 text-xs`} value="" disabled={busy}
                                                            aria-label={`Move ${st.FirstName} ${st.LastName} to another cohort`}
                                                            onChange={(e) => moveTo(st, e.target.value)}>
                                                        <option value="">Choose a cohort…</option>
                                                        {otherCohorts.map((c) => <option key={c.CohortID} value={c.CohortID}>{c.CohortName}</option>)}
                                                    </select>
                                                </td>
                                            )}
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </section>
            </div>
        </OrgModal>
    );
}

function SuggestionEditor({ title, hint, initial, allowEmpty = false, onSave, onClose }) {
    const s = useOrgStyles();
    const [rows, setRows] = useState(() => (initial.length ? initial : [{}]).map((it, i) => ({
        key: `r${i}`, title: it.Title || "", description: it.Description || "",
    })));
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState("");

    const update = (i, k, v) => setRows((r) => r.map((row, j) => (j === i ? { ...row, [k]: v } : row)));
    const move = (i, d) => setRows((r) => {
        const next = [...r];
        [next[i], next[i + d]] = [next[i + d], next[i]];
        return next;
    });
    const remove = (i) => setRows((r) => r.filter((_, j) => j !== i));
    const add = () => setRows((r) => [...r, { key: `n${Date.now()}`, title: "", description: "" }]);

    const save = async (e) => {
        e.preventDefault();
        const items = rows.filter((r) => r.title.trim() || r.description.trim())
            .map((r) => ({ title: r.title.trim(), description: r.description.trim() }));
        if (items.some((it) => !it.title)) { setError("Each suggestion needs a title."); return; }
        if (!items.length && !allowEmpty) { setError("Add at least one suggestion."); return; }
        setSaving(true);
        setError("");
        try {
            await onSave(items);
        } catch (err) {
            setError(apiError(err));
            setSaving(false);
        }
    };

    return (
        <OrgModal isOpen onClose={onClose} title={title} wide
                  footer={<>
                      <button type="button" className={s.btnSecondary} onClick={onClose}>Cancel</button>
                      <button type="submit" form="suggestion-editor" className={s.btnPrimary} disabled={saving}>
                          {saving && <Loader2 size={14} className="animate-spin" />} Save
                      </button>
                  </>}>
            <p className={`mb-4 text-sm ${s.muted}`}>{hint}</p>
            <Alert>{error}</Alert>
            <form id="suggestion-editor" onSubmit={save} className="space-y-4">
                <ol className="space-y-3">
                    {rows.map((row, i) => (
                        <li key={row.key} className={`rounded-xl border p-3 ${s.subPanel}`}>
                            <div className="flex items-start gap-2">
                                <span className={`mt-2 w-5 shrink-0 text-right text-sm tabular-nums ${s.muted}`}>{i + 1}.</span>
                                <div className="min-w-0 flex-1 space-y-2">
                                    <input className={s.input} value={row.title} maxLength={255} placeholder="Title, like Update your resume"
                                           aria-label={`Suggestion ${i + 1} title`} onChange={(e) => update(i, "title", e.target.value)} />
                                    <textarea className={s.input} rows={2} value={row.description} maxLength={2000} placeholder="Details (optional)"
                                              aria-label={`Suggestion ${i + 1} details`} onChange={(e) => update(i, "description", e.target.value)} />
                                </div>
                                <div className="flex shrink-0 flex-col gap-1">
                                    <button type="button" className={s.btnSm} onClick={() => move(i, -1)} disabled={i === 0} aria-label={`Move suggestion ${i + 1} up`} title="Move up"><ArrowUp size={12} /></button>
                                    <button type="button" className={s.btnSm} onClick={() => move(i, 1)} disabled={i === rows.length - 1} aria-label={`Move suggestion ${i + 1} down`} title="Move down"><ArrowDown size={12} /></button>
                                    <button type="button" className={s.btnSm} onClick={() => remove(i)} aria-label={`Remove suggestion ${i + 1}`} title="Remove"><Trash2 size={12} /></button>
                                </div>
                            </div>
                        </li>
                    ))}
                </ol>
                <div className="flex flex-wrap items-center gap-3">
                    <button type="button" className={s.btnSecondary} onClick={add} disabled={rows.length >= MAX_SUGGESTIONS}>
                        <Plus size={14} /> Add suggestion
                    </button>
                    {/* old <span className={`text-xs ${s.muted}`}>Up to {MAX_SUGGESTIONS}. Students see them in this order.</span> */}
                    <span className={`text-xs ${s.muted}`}>Up to {MAX_SUGGESTIONS}. Clients see them in this order.</span>
                </div>
            </form>
        </OrgModal>
    );
}

export function ResourceEditor({ title, hint, initial, allowEmpty = false, onSave, onClose }) {
    const s = useOrgStyles();
    const [rows, setRows] = useState(() => (initial.length ? initial : [{}]).map((it, i) => ({
        key: `r${i}`, title: it.Title || "", url: it.Url || "", description: it.Description || "",
    })));
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState("");

    const update = (i, k, v) => setRows((r) => r.map((row, j) => (j === i ? { ...row, [k]: v } : row)));
    const move = (i, d) => setRows((r) => {
        const next = [...r];
        [next[i], next[i + d]] = [next[i + d], next[i]];
        return next;
    });
    const remove = (i) => setRows((r) => r.filter((_, j) => j !== i));
    const add = () => setRows((r) => [...r, { key: `n${Date.now()}`, title: "", url: "", description: "" }]);

    const save = async (e) => {
        e.preventDefault();
        const items = rows.filter((r) => r.title.trim() || r.url.trim() || r.description.trim())
            .map((r) => ({ title: r.title.trim(), url: r.url.trim(), description: r.description.trim() }));
        if (items.some((it) => !it.title || !it.url)) { setError("Each resource needs a title and a link."); return; }
        if (items.some((it) => !/^https?:\/\/\S+$/i.test(it.url))) { setError("Each link must start with http:// or https:// and have no spaces."); return; }
        if (!items.length && !allowEmpty) { setError("Add at least one resource."); return; }
        setSaving(true);
        setError("");
        try {
            await onSave(items);
        } catch (err) {
            setError(apiError(err));
            setSaving(false);
        }
    };

    return (
        <OrgModal isOpen onClose={onClose} title={title} wide
                  footer={<>
                      <button type="button" className={s.btnSecondary} onClick={onClose}>Cancel</button>
                      <button type="submit" form="resource-editor" className={s.btnPrimary} disabled={saving}>
                          {saving && <Loader2 size={14} className="animate-spin" />} Save
                      </button>
                  </>}>
            <p className={`mb-4 text-sm ${s.muted}`}>{hint}</p>
            <Alert>{error}</Alert>
            <form id="resource-editor" onSubmit={save} className="space-y-4">
                <ol className="space-y-3">
                    {rows.map((row, i) => (
                        <li key={row.key} className={`rounded-xl border p-3 ${s.subPanel}`}>
                            <div className="flex items-start gap-2">
                                <span className={`mt-2 w-5 shrink-0 text-right text-sm tabular-nums ${s.muted}`}>{i + 1}.</span>
                                <div className="min-w-0 flex-1 space-y-2">
                                    <input className={s.input} value={row.title} maxLength={300} placeholder="Title, like Career center job board"
                                           aria-label={`Resource ${i + 1} title`} onChange={(e) => update(i, "title", e.target.value)} />
                                    <input type="url" className={s.input} value={row.url} maxLength={1000} placeholder="https://…"
                                           aria-label={`Resource ${i + 1} link`} onChange={(e) => update(i, "url", e.target.value)} />
                                    <textarea className={s.input} rows={2} value={row.description} maxLength={1000} placeholder="Details (optional)"
                                              aria-label={`Resource ${i + 1} details`} onChange={(e) => update(i, "description", e.target.value)} />
                                </div>
                                <div className="flex shrink-0 flex-col gap-1">
                                    <button type="button" className={s.btnSm} onClick={() => move(i, -1)} disabled={i === 0} aria-label={`Move resource ${i + 1} up`} title="Move up"><ArrowUp size={12} /></button>
                                    <button type="button" className={s.btnSm} onClick={() => move(i, 1)} disabled={i === rows.length - 1} aria-label={`Move resource ${i + 1} down`} title="Move down"><ArrowDown size={12} /></button>
                                    <button type="button" className={s.btnSm} onClick={() => remove(i)} aria-label={`Remove resource ${i + 1}`} title="Remove"><Trash2 size={12} /></button>
                                </div>
                            </div>
                        </li>
                    ))}
                </ol>
                <div className="flex flex-wrap items-center gap-3">
                    <button type="button" className={s.btnSecondary} onClick={add} disabled={rows.length >= MAX_RESOURCES}>
                        <Plus size={14} /> Add resource
                    </button>
                    {/* old <span className={`text-xs ${s.muted}`}>Up to {MAX_RESOURCES}. Students see them in this order.</span> */}
                    <span className={`text-xs ${s.muted}`}>Up to {MAX_RESOURCES}. Clients see them in this order.</span>
                </div>
            </form>
        </OrgModal>
    );
}
